/**
 * Binary codec for the generated world. Pure (no fs / fetch) so it runs in the
 * browser, Node and tests alike.
 *
 * world.v1.bin (little-endian, sections 4-byte aligned):
 *   'SAW1' u32 format f64 hexR f64 width f64 height u32 rows u32 cols
 *   u32 N u32 M u32 L u32 C u32 K
 *   cells u8[rows*cols] (1 = land, 0 = water)
 *   capBase f32[N]  areaW f32[N]  countryId u8[N]  coreOf u8[N]
 *   portWater i32[N]  linkStart i32[N+1]  linkHex i32[L]  linkW f32[L]
 *   wnbrOverrides i32[C*3] (water, slot, target water)  — canal / seaway links
 *   nbrCuts i32[K*2] (land hex, slot)  — lattice edges across water (straits), nbr = −1
 * Everything else (q, r, centres, nbr, wnbr, coastal) is derived from the lattice.
 *
 * coast.v1.bin: 'SAC1' u32 format u32 nCoast u32 nBorders, then per polyline
 *   varint pointCount + zigzag-varint deltas of (x, y) quantised to 1/100 mu
 *   (delta chain continues across polylines within a section).
 */
import {
  COAST_MAGIC,
  COAST_QUANT,
  FORMAT_VERSION,
  WORLD_MAGIC,
  type EncodedWorld,
  type LoadedWorld,
  type MapExtras,
  type WorldMeta,
} from './format';
import { HEX_DIRS, axialCenterX, axialCenterY, axialToCell, makeLattice, type Lattice } from './lattice';

const HEADER_BYTES = 4 + 4 + 8 * 3 + 4 * 7;

const align4 = (n: number) => (n + 3) & ~3;

// ---------------------------------------------------------------- decode

export interface LatticeGeometry {
  cellIndex: Int32Array;
  q: Int16Array;
  r: Int16Array;
  cx: Float32Array;
  cy: Float32Array;
  nbr: Int32Array;
  coastal: Uint8Array;
  wq: Int16Array;
  wr: Int16Array;
  wcx: Float32Array;
  wcy: Float32Array;
  wnbr: Int32Array;
}

/** Derive all per-hex geometry from the lattice and its land/water cell mask. */
export function deriveGeometry(lat: Lattice, cells: Uint8Array): LatticeGeometry {
  const total = lat.rows * lat.cols;
  if (cells.length !== total) throw new Error(`cells length ${cells.length} != ${total}`);
  const cellIndex = new Int32Array(total);
  let n = 0;
  let m = 0;
  for (let c = 0; c < total; c++) cellIndex[c] = cells[c] ? n++ : -1 - m++;
  const q = new Int16Array(n);
  const r = new Int16Array(n);
  const cx = new Float32Array(n);
  const cy = new Float32Array(n);
  const wq = new Int16Array(m);
  const wr = new Int16Array(m);
  const wcx = new Float32Array(m);
  const wcy = new Float32Array(m);
  const nbr = new Int32Array(n * 6).fill(-1);
  const wnbr = new Int32Array(m * 6).fill(-1);
  const coastal = new Uint8Array(n);
  for (let c = 0; c < total; c++) {
    const row = Math.floor(c / lat.cols);
    const hq = c - row * lat.cols - Math.floor(row / 2);
    const x = axialCenterX(lat, hq, row);
    const y = axialCenterY(lat, row);
    const idx = cellIndex[c];
    if (idx >= 0) {
      q[idx] = hq;
      r[idx] = row;
      cx[idx] = x;
      cy[idx] = y;
    } else {
      const w = -1 - idx;
      wq[w] = hq;
      wr[w] = row;
      wcx[w] = x;
      wcy[w] = y;
    }
    for (let d = 0; d < 6; d++) {
      const nc = axialToCell(lat, hq + HEX_DIRS[d][0], row + HEX_DIRS[d][1]);
      if (nc < 0) continue;
      const ni = cellIndex[nc];
      if (idx >= 0) {
        if (ni >= 0) nbr[idx * 6 + d] = ni;
        else coastal[idx] = 1;
      } else if (ni < 0) {
        wnbr[(-1 - idx) * 6 + d] = -1 - ni;
      }
    }
  }
  return { cellIndex, q, r, cx, cy, nbr, coastal, wq, wr, wcx, wcy, wnbr };
}

/** Water hexes in the largest connected water component (ties → lowest index). */
export function mainNavComponent(wnbr: Int32Array, m: number): Uint8Array {
  const comp = new Int32Array(m).fill(-1);
  const stack: number[] = [];
  let best = -1;
  let bestSize = 0;
  let id = 0;
  for (let s = 0; s < m; s++) {
    if (comp[s] >= 0) continue;
    let size = 0;
    comp[s] = id;
    stack.push(s);
    while (stack.length) {
      const w = stack.pop()!;
      size++;
      for (let d = 0; d < 6; d++) {
        const o = wnbr[w * 6 + d];
        if (o >= 0 && comp[o] < 0) {
          comp[o] = id;
          stack.push(o);
        }
      }
    }
    if (size > bestSize) {
      bestSize = size;
      best = id;
    }
    id++;
  }
  const nav = new Uint8Array(m);
  for (let w = 0; w < m; w++) nav[w] = comp[w] === best ? 1 : 0;
  return nav;
}

export function decodeWorld(
  binInput: ArrayBuffer | Uint8Array,
  meta: WorldMeta,
  coastInput: ArrayBuffer | Uint8Array,
): LoadedWorld {
  const bin = toBytes(binInput);
  const dv = new DataView(bin.buffer, bin.byteOffset, bin.byteLength);
  if (readMagic(bin, 0) !== WORLD_MAGIC) throw new Error('world bin: bad magic');
  const format = dv.getUint32(4, true);
  if (format !== FORMAT_VERSION || meta.format !== FORMAT_VERSION) throw new Error(`world: unsupported format ${format}`);
  const hexR = dv.getFloat64(8, true);
  const width = dv.getFloat64(16, true);
  const height = dv.getFloat64(24, true);
  const rows = dv.getUint32(32, true);
  const cols = dv.getUint32(36, true);
  const n = dv.getUint32(40, true);
  const m = dv.getUint32(44, true);
  const nLinks = dv.getUint32(48, true);
  const nOverrides = dv.getUint32(52, true);
  const nCuts = dv.getUint32(56, true);
  const lattice = makeLattice(hexR, width, height);
  if (lattice.rows !== rows || lattice.cols !== cols) throw new Error('world bin: lattice mismatch');

  let off = HEADER_BYTES;
  const cells = bin.slice(off, off + rows * cols);
  off = align4(off + rows * cols);
  const geo = deriveGeometry(lattice, cells);
  if (geo.q.length !== n || geo.wq.length !== m) throw new Error('world bin: hex count mismatch');

  const f32 = (len: number) => {
    const a = new Float32Array(len);
    for (let i = 0; i < len; i++, off += 4) a[i] = dv.getFloat32(off, true);
    return a;
  };
  const i32 = (len: number) => {
    const a = new Int32Array(len);
    for (let i = 0; i < len; i++, off += 4) a[i] = dv.getInt32(off, true);
    return a;
  };
  const u8 = (len: number) => {
    const a = bin.slice(off, off + len);
    off += len;
    return a;
  };

  const capBase = f32(n);
  const areaW = f32(n);
  const countryId = u8(n);
  const coreOf = u8(n);
  off = align4(off);
  const portWater = i32(n);
  const linkStart = i32(n + 1);
  const linkHex = i32(nLinks);
  const linkW = f32(nLinks);
  const overrides = i32(nOverrides * 3);
  const cuts = i32(nCuts * 2);
  if (off !== bin.byteLength) throw new Error(`world bin: trailing bytes (${bin.byteLength - off})`);
  for (let k = 0; k < nOverrides; k++) {
    geo.wnbr[overrides[k * 3] * 6 + overrides[k * 3 + 1]] = overrides[k * 3 + 2];
  }
  for (let k = 0; k < nCuts; k++) geo.nbr[cuts[k * 2] * 6 + cuts[k * 2 + 1]] = -1;

  const { coast, borders } = decodeCoast(coastInput);
  const extras: MapExtras = {
    lattice,
    cellIndex: geo.cellIndex,
    straits: meta.straits,
    canals: meta.canals,
    navigable: mainNavComponent(geo.wnbr, m),
    regionBalance: meta.regionBalance,
  };
  return {
    mapVersion: meta.mapVersion,
    hexR,
    width,
    height,
    landCount: n,
    q: geo.q,
    r: geo.r,
    cx: geo.cx,
    cy: geo.cy,
    capBase,
    areaW,
    countryId,
    coreOf,
    coastal: geo.coastal,
    nbr: geo.nbr,
    linkStart,
    linkHex,
    linkW,
    portWater,
    waterCount: m,
    wq: geo.wq,
    wr: geo.wr,
    wcx: geo.wcx,
    wcy: geo.wcy,
    wnbr: geo.wnbr,
    regions: meta.regions,
    cities: meta.cities,
    countries: meta.countries,
    coast,
    borders,
    extras,
  };
}

export function decodeCoast(input: ArrayBuffer | Uint8Array): { coast: Float32Array[]; borders: Float32Array[] } {
  const b = toBytes(input);
  const dv = new DataView(b.buffer, b.byteOffset, b.byteLength);
  if (readMagic(b, 0) !== COAST_MAGIC) throw new Error('coast bin: bad magic');
  if (dv.getUint32(4, true) !== FORMAT_VERSION) throw new Error('coast bin: unsupported format');
  const counts = [dv.getUint32(8, true), dv.getUint32(12, true)];
  let off = 16;
  const varint = () => {
    let v = 0;
    let shift = 0;
    for (;;) {
      const byte = b[off++];
      v += (byte & 0x7f) * 2 ** shift;
      if (byte < 0x80) return v;
      shift += 7;
    }
  };
  const zz = () => {
    const u = varint();
    return u % 2 === 0 ? u / 2 : -(u + 1) / 2;
  };
  const sections = counts.map((count) => {
    const lines: Float32Array[] = [];
    let x = 0;
    let y = 0;
    for (let i = 0; i < count; i++) {
      const pts = varint();
      const line = new Float32Array(pts * 2);
      for (let p = 0; p < pts; p++) {
        x += zz();
        y += zz();
        line[p * 2] = x / COAST_QUANT;
        line[p * 2 + 1] = y / COAST_QUANT;
      }
      lines.push(line);
    }
    return lines;
  });
  if (off !== b.byteLength) throw new Error('coast bin: trailing bytes');
  return { coast: sections[0], borders: sections[1] };
}

// ---------------------------------------------------------------- encode

/** Inverse of decodeWorld. Verifies that derivable fields match the lattice. */
export function encodeWorld(w: LoadedWorld): EncodedWorld {
  const lat = w.extras.lattice;
  const total = lat.rows * lat.cols;
  const cells = new Uint8Array(total);
  for (let i = 0; i < w.landCount; i++) {
    const c = axialToCell(lat, w.q[i], w.r[i]);
    if (c < 0) throw new Error(`land hex ${i} outside lattice`);
    cells[c] = 1;
  }
  const geo = deriveGeometry(lat, cells);
  assertSame('q', geo.q, w.q);
  assertSame('r', geo.r, w.r);
  assertSame('coastal', geo.coastal, w.coastal);
  assertSame('wq', geo.wq, w.wq);
  assertSame('wr', geo.wr, w.wr);
  assertSame('cx', geo.cx, w.cx);
  assertSame('wcy', geo.wcy, w.wcy);
  const cuts: number[] = [];
  for (let k = 0; k < w.nbr.length; k++) {
    if (geo.nbr[k] === w.nbr[k]) continue;
    if (w.nbr[k] !== -1) throw new Error(`encodeWorld: nbr[${k}] = ${w.nbr[k]} is not a lattice neighbour`);
    cuts.push(Math.floor(k / 6), k % 6);
  }
  const overrides: number[] = [];
  for (let k = 0; k < w.wnbr.length; k++) {
    if (geo.wnbr[k] === w.wnbr[k]) continue;
    if (geo.wnbr[k] !== -1) throw new Error(`wnbr override replaces a lattice neighbour at ${k}`);
    overrides.push(Math.floor(k / 6), k % 6, w.wnbr[k]);
  }
  const n = w.landCount;
  const nLinks = w.linkHex.length;
  const size =
    align4(HEADER_BYTES + total) + n * 8 + align4(n * 2) + n * 4 + (n + 1) * 4 + nLinks * 8 + overrides.length * 4 + cuts.length * 4;
  const bin = new Uint8Array(size);
  const dv = new DataView(bin.buffer);
  writeMagic(bin, 0, WORLD_MAGIC);
  dv.setUint32(4, FORMAT_VERSION, true);
  dv.setFloat64(8, w.hexR, true);
  dv.setFloat64(16, w.width, true);
  dv.setFloat64(24, w.height, true);
  [lat.rows, lat.cols, n, w.waterCount, nLinks, overrides.length / 3, cuts.length / 2].forEach((v, i) => dv.setUint32(32 + i * 4, v, true));
  let off = HEADER_BYTES;
  bin.set(cells, off);
  off = align4(off + total);
  const putF32 = (a: ArrayLike<number>) => {
    for (let i = 0; i < a.length; i++, off += 4) dv.setFloat32(off, a[i], true);
  };
  const putI32 = (a: ArrayLike<number>) => {
    for (let i = 0; i < a.length; i++, off += 4) dv.setInt32(off, a[i], true);
  };
  putF32(w.capBase);
  putF32(w.areaW);
  bin.set(w.countryId, off);
  off += n;
  bin.set(w.coreOf, off);
  off = align4(off + n);
  putI32(w.portWater);
  putI32(w.linkStart);
  putI32(w.linkHex);
  putF32(w.linkW);
  putI32(overrides);
  putI32(cuts);
  if (off !== size) throw new Error('encodeWorld: size mismatch');

  const meta: WorldMeta = {
    format: FORMAT_VERSION,
    mapVersion: w.mapVersion,
    regions: w.regions,
    cities: w.cities,
    countries: w.countries,
    straits: w.extras.straits,
    canals: w.extras.canals,
    regionBalance: w.extras.regionBalance,
  };
  return { bin, meta, coast: encodeCoast(w.coast, w.borders) };
}

export function encodeCoast(coast: Float32Array[], borders: Float32Array[]): Uint8Array {
  const out: number[] = [];
  const varint = (v: number) => {
    while (v >= 0x80) {
      out.push((v % 0x80) | 0x80);
      v = Math.floor(v / 0x80);
    }
    out.push(v);
  };
  const zz = (v: number) => varint(v >= 0 ? v * 2 : -v * 2 - 1);
  for (const section of [coast, borders]) {
    let px = 0;
    let py = 0;
    for (const line of section) {
      varint(line.length / 2);
      for (let p = 0; p < line.length; p += 2) {
        const x = Math.round(line[p] * COAST_QUANT);
        const y = Math.round(line[p + 1] * COAST_QUANT);
        zz(x - px);
        zz(y - py);
        px = x;
        py = y;
      }
    }
  }
  const bytes = new Uint8Array(16 + out.length);
  const dv = new DataView(bytes.buffer);
  writeMagic(bytes, 0, COAST_MAGIC);
  dv.setUint32(4, FORMAT_VERSION, true);
  dv.setUint32(8, coast.length, true);
  dv.setUint32(12, borders.length, true);
  bytes.set(out, 16);
  return bytes;
}

// ---------------------------------------------------------------- helpers

function toBytes(x: ArrayBuffer | Uint8Array): Uint8Array {
  return x instanceof Uint8Array ? x : new Uint8Array(x);
}

function readMagic(b: Uint8Array, off: number): string {
  return String.fromCharCode(b[off], b[off + 1], b[off + 2], b[off + 3]);
}

function writeMagic(b: Uint8Array, off: number, magic: string): void {
  for (let i = 0; i < 4; i++) b[off + i] = magic.charCodeAt(i);
}

function assertSame(name: string, a: ArrayLike<number>, b: ArrayLike<number>): void {
  if (a.length !== b.length) throw new Error(`encodeWorld: ${name} length ${b.length}, lattice gives ${a.length}`);
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) throw new Error(`encodeWorld: ${name}[${i}] = ${b[i]}, lattice gives ${a[i]}`);
  }
}
